import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { DownloadingOutlined } from '@mui/icons-material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import AssignmentIcon from '@mui/icons-material/Assignment';
import FolderIcon from '@mui/icons-material/Folder';
import FormatListBulletedOutlinedIcon from '@mui/icons-material/FormatListBulletedOutlined';
import HomeIcon from '@mui/icons-material/Home';
import MemoryIcon from '@mui/icons-material/Memory';
import PeopleIcon from '@mui/icons-material/People';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import {
	Box,
	Divider,
	IconButton,
	List,
	ListItem,
	Typography,
} from '@mui/material';

import { INavbar } from 'src/components/common/types/navbarTypes';
import WithRoleAccess from 'src/routes/WithRoleAccess';
import { RootState } from 'src/store';

const Navbar: React.FC<INavbar> = ({
	hideNavbar,
}: INavbar): React.JSX.Element => {
	const navigate = useNavigate();
	const { siteDetails } = useSelector((state: RootState) => state.site);
	const { siteSlug } = useParams();

	return (
		<List>
			<Box
				sx={{
					width: '22rem',
					display: 'flex',
					flexDirection: 'column',
					gap: 2,
				}}
				data-testid='Navbar'
			>
				<ListItem
					onClick={hideNavbar}
					sx={{
						cursor: 'pointer',
						display: 'flex',
						flexDirection: 'row-reverse',
					}}
					data-testid='NavbarItemBack'
				>
					<IconButton>
						<ArrowBackIosIcon fontSize='small' />
					</IconButton>
				</ListItem>
				<Divider sx={{ marginTop: -0.5 }} />
				<ListItem
					onClick={() => {
						hideNavbar();
						navigate('/');
					}}
					sx={{
						cursor: 'pointer',
						display: 'flex',
						gap: 2,
						':hover': {
							background: '#EEEEEE',
							color: '#000000',
						},
					}}
				>
					<HomeIcon color='action' />
					<Typography variant='body1' sx={{ color: '#00000099' }}>
						Home
					</Typography>
				</ListItem>
				{siteSlug && siteDetails && (
					<>
						<ListItem
							sx={{
								cursor: 'pointer',
								display: 'flex',
								gap: 2,
								':hover': {
									background: '#EEEEEE',
									color: '#000000',
								},
							}}
						>
							<FolderIcon color='action' />
							<Typography
								variant='body1'
								sx={{ color: '#00000099' }}
							>
								{siteDetails.name}
							</Typography>
						</ListItem>
						<ListItem
							onClick={() => {
								hideNavbar();
								navigate(`/site/${siteSlug}`);
							}}
							sx={{
								cursor: 'pointer',
								display: 'flex',
								gap: 2,
								pl: 8,
								':hover': {
									background: '#EEEEEE',
									color: '#000000',
								},
							}}
						>
							<PeopleAltOutlinedIcon color='action' />
							<Typography
								variant='body1'
								sx={{ color: '#00000099' }}
							>
								Patients
							</Typography>
						</ListItem>

						<WithRoleAccess
							allowedSiteRoles={[
								'super_admin',
								'admin',
								'annotator',
							]}
						>
							<ListItem
								onClick={() => {
									hideNavbar();
									navigate(
										`/site/${siteSlug}/study-assignments`,
									);
								}}
								sx={{
									cursor: 'pointer',
									display: 'flex',
									gap: 2,
									pl: 8,
									':hover': {
										background: '#EEEEEE',
										color: '#000000',
									},
								}}
							>
								<AssignmentIcon color='action' />
								<Typography
									variant='body1'
									sx={{ color: '#00000099' }}
								>
									Study Assignments
								</Typography>
							</ListItem>
						</WithRoleAccess>

						<WithRoleAccess
							allowedSiteRoles={['super_admin', 'admin']}
						>
							<ListItem
								onClick={() => {
									hideNavbar();
									navigate(
										`/site/${siteSlug}/questionnaire-templates`,
									);
								}}
								sx={{
									cursor: 'pointer',
									display: 'flex',
									gap: 2,
									pl: 8,
									':hover': {
										background: '#EEEEEE',
										color: '#000000',
									},
								}}
							>
								<FormatListBulletedOutlinedIcon color='action' />
								<Typography
									variant='body1'
									sx={{ color: '#00000099' }}
								>
									Questionnaire Templates
								</Typography>
							</ListItem>

							<ListItem
								onClick={() => {
									hideNavbar();
									navigate(`/site/${siteSlug}/settings`);
								}}
								sx={{
									cursor: 'pointer',
									display: 'flex',
									gap: 2,
									pl: 8,
									':hover': {
										background: '#EEEEEE',
										color: '#000000',
									},
								}}
							>
								<SettingsOutlinedIcon color='action' />
								<Typography
									variant='body1'
									sx={{ color: '#00000099' }}
								>
									Site Settings
								</Typography>
							</ListItem>

							<ListItem
								onClick={() => {
									hideNavbar();
									navigate(`/site/${siteSlug}/site-download`);
								}}
								sx={{
									cursor: 'pointer',
									display: 'flex',
									gap: 2,
									pl: 8,
									':hover': {
										background: '#EEEEEE',
										color: '#000000',
									},
								}}
							>
								<DownloadingOutlined color='action' />
								<Typography
									variant='body1'
									sx={{ color: '#00000099' }}
								>
									Downloads
								</Typography>
							</ListItem>

							<ListItem
								onClick={() => {
									hideNavbar();
									navigate(
										`/site/${siteSlug}/annotation-label`,
									);
								}}
								sx={{
									cursor: 'pointer',
									display: 'flex',
									gap: 2,
									pl: 8,
									':hover': {
										background: '#EEEEEE',
										color: '#000000',
									},
								}}
							>
								<FormatListBulletedOutlinedIcon color='action' />
								<Typography
									variant='body1'
									sx={{ color: '#00000099' }}
								>
									Annotation Labels
								</Typography>
							</ListItem>
							<ListItem
								onClick={() => {
									hideNavbar();
									navigate(
										`/site/${siteSlug}/annotation-templates`,
									);
								}}
								sx={{
									cursor: 'pointer',
									display: 'flex',
									gap: 2,
									pl: 8,
									':hover': {
										background: '#EEEEEE',
										color: '#000000',
									},
								}}
							>
								<FormatListBulletedOutlinedIcon color='action' />
								<Typography
									variant='body1'
									sx={{ color: '#00000099' }}
								>
									Annotation Templates
								</Typography>
							</ListItem>
							<ListItem
								onClick={() => {
									hideNavbar();
									navigate(`/site/${siteSlug}/ai-models`);
								}}
								sx={{
									cursor: 'pointer',
									display: 'flex',
									gap: 2,
									pl: 8,
									':hover': {
										background: '#EEEEEE',
										color: '#000000',
									},
								}}
							>
								<MemoryIcon color='action' />
								<Typography
									variant='body1'
									sx={{ color: '#00000099' }}
								>
									AI Models
								</Typography>
							</ListItem>
						</WithRoleAccess>
					</>
				)}
				<WithRoleAccess allowedGlobalRoles={['super_admin']}>
					<ListItem
						onClick={() => {
							hideNavbar();
							navigate('/users');
						}}
						sx={{
							cursor: 'pointer',
							display: 'flex',
							gap: 2,
							':hover': {
								background: '#EEEEEE',
								color: '#000000',
							},
						}}
					>
						<PeopleIcon color='action' />
						<Typography variant='body1' sx={{ color: '#00000099' }}>
							Users
						</Typography>
					</ListItem>
				</WithRoleAccess>
			</Box>
		</List>
	);
};

export default Navbar;
